import React from 'react';
import { createPortal } from 'react-dom';
import { View } from '../../types';
import { RODDO_GUIDED_TOUR_STEPS } from './tourSteps';

interface Props {
  activeView: View;
  isDarkMode: boolean;
  onNavigate: (view: View) => void;
  onFinish: () => void | Promise<void>;
  onSkip: () => void | Promise<void>;
}

type Rect = {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
} | null;

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value));

export default function RoddoGuidedTour({
  activeView,
  isDarkMode,
  onNavigate,
  onFinish,
  onSkip,
}: Props) {
  const [index, setIndex] = React.useState(0);
  const [rect, setRect] = React.useState<Rect>(null);
  const [settled, setSettled] = React.useState(false);

  const step = RODDO_GUIDED_TOUR_STEPS[index];
  const isLast = index === RODDO_GUIDED_TOUR_STEPS.length - 1;
  const isButtonIntro = step?.pulse === 'target';

  React.useEffect(() => {
    if (!step) return;
    if (activeView !== step.view) onNavigate(step.view);
  }, [activeView, onNavigate, step]);

  React.useEffect(() => {
    setSettled(false);
    if (!step || activeView !== step.view) return;

    const delay = step.revealTarget ? 420 : isButtonIntro ? 130 : 260;
    const timer = window.setTimeout(() => setSettled(true), delay);
    return () => window.clearTimeout(timer);
  }, [activeView, index, isButtonIntro, step]);

  React.useEffect(() => {
    if (!step?.revealTarget || activeView !== step.view) return;

    let cancelled = false;
    const reveal = () => {
      if (cancelled) return;
      const node = document.querySelector<HTMLElement>(
        `[data-roddo-tour="${step.revealTarget}"]`,
      );
      if (!node) return;
      try {
        node.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } catch {
        node.scrollIntoView();
      }
    };

    const first = window.setTimeout(reveal, 180);
    const second = window.setTimeout(reveal, 520);
    return () => {
      cancelled = true;
      window.clearTimeout(first);
      window.clearTimeout(second);
    };
  }, [activeView, index, step]);

  React.useLayoutEffect(() => {
    if (!step || !isButtonIntro) {
      setRect(null);
      return;
    }

    let cancelled = false;
    let timer = 0;

    const measure = () => {
      if (cancelled) return;
      const node = step.target
        ? document.querySelector<HTMLElement>(`[data-roddo-tour="${step.target}"]`)
        : null;

      if (!node) {
        setRect(null);
        return;
      }

      const r = node.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) {
        setRect(null);
        return;
      }

      const pad = 6;
      setRect({
        top: Math.max(5, r.top - pad),
        left: Math.max(5, r.left - pad),
        right: Math.min(window.innerWidth - 5, r.right + pad),
        bottom: Math.min(window.innerHeight - 5, r.bottom + pad),
        width: Math.max(20, r.width + pad * 2),
        height: Math.max(20, r.height + pad * 2),
      });
    };

    const schedule = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(measure, 90);
    };

    measure();
    schedule();
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    window.visualViewport?.addEventListener('resize', schedule);
    window.visualViewport?.addEventListener('scroll', schedule);

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      window.visualViewport?.removeEventListener('resize', schedule);
      window.visualViewport?.removeEventListener('scroll', schedule);
      observer.disconnect();
    };
  }, [activeView, index, isButtonIntro, step]);

  if (!step || typeof document === 'undefined') return null;

  const accent = isDarkMode ? '#7057E8' : '#2F80ED';
  const accentGlow = isDarkMode
    ? 'rgba(112,87,232,.48)'
    : 'rgba(47,128,237,.42)';
  const accentSoft = isDarkMode
    ? 'rgba(112,87,232,.20)'
    : 'rgba(47,128,237,.18)';
  const cardBg = isDarkMode ? 'rgba(18,26,43,.97)' : 'rgba(255,252,247,.98)';
  const text = isDarkMode ? '#F4F6FA' : '#30271E';
  const muted = isDarkMode ? '#AEB8C9' : '#6E6255';
  const border = isDarkMode
    ? 'rgba(180,195,220,.18)'
    : 'rgba(74,58,42,.16)';
  const dim = isDarkMode ? 'rgba(4,8,16,.34)' : 'rgba(37,29,20,.22)';
  const demoTint = isDarkMode ? 'rgba(4,8,16,.16)' : 'rgba(47,38,28,.10)';

  const viewportW = window.visualViewport?.width || window.innerWidth;
  const viewportH = window.visualViewport?.height || window.innerHeight;
  const sideInset = viewportW <= 390 ? 14 : 18;
  const cardWidth = Math.min(356, Math.max(276, viewportW - sideInset * 2));
  const cardHeightEstimate = 222;

  // Keep the teaching card in one stable place for every step.
  // The pulse moves to the feature; the user's reading/Next target does not.
  const headerInset = 72;
  const navInset = 76;
  const safeTop = headerInset + 14;
  const safeBottom = navInset + 14;
  const safeHeight = Math.max(220, viewportH - safeTop - safeBottom);
  const cardCenterY = clamp(
    safeTop + safeHeight / 2,
    safeTop + cardHeightEstimate / 2,
    viewportH - safeBottom - cardHeightEstimate / 2,
  );

  const pulseX = rect ? rect.left + rect.width / 2 : viewportW / 2;
  const pulseY = rect ? rect.top + rect.height / 2 : viewportH / 2;
  const targetPulseSize = rect
    ? clamp(Math.max(rect.width, rect.height) + 22, 54, 94)
    : 62;

  const go = (nextIndex: number) => {
    const next = RODDO_GUIDED_TOUR_STEPS[nextIndex];
    if (!next) return;
    setSettled(false);
    setIndex(nextIndex);
    if (activeView !== next.view) onNavigate(next.view);
  };

  const handlePrimary = () => {
    if (isLast) {
      void onFinish();
      return;
    }
    go(index + 1);
  };

  const card = settled ? (
    <div
      className="roddo-tour-card pointer-events-auto absolute flex flex-col rounded-[17px] border px-4 py-3.5"
      style={{
        width: cardWidth,
        minHeight: 214,
        left: '50%',
        top: cardCenterY,
        transform: 'translate(-50%, -50%)',
        background: cardBg,
        borderColor: border,
        color: text,
        boxShadow: isDarkMode
          ? '0 18px 42px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.06)'
          : '0 16px 36px rgba(53,40,25,.14), inset 0 1px 0 rgba(255,255,255,.80)',
        maxHeight: 'min(46dvh, 270px)',
        overflow: 'hidden',
      }}
    >
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-0.5">
        <div className="relative px-7 text-center">
          <div
            className="text-[9px] font-black uppercase tracking-[0.17em]"
            style={{ color: accent }}
          >
            {step.eyebrow || 'Roddo tour'}
          </div>
          <h2 className="mt-1 text-[18px] font-black leading-tight tracking-[-0.025em] min-[390px]:text-[19px]">
            {step.title}
          </h2>
          <div
            className="absolute right-0 top-0 text-[9px] font-black"
            style={{ color: muted }}
          >
            {index + 1}/{RODDO_GUIDED_TOUR_STEPS.length}
          </div>
        </div>

        <p className="mx-auto mt-2 max-w-[302px] text-center text-[12.5px] font-semibold leading-[1.45]" style={{ color: muted }}>
          {step.body}
        </p>
        {step.note ? (
          <p className="mx-auto mt-2 max-w-[290px] text-center text-[10.5px] font-bold leading-4" style={{ color: accent }}>
            {step.note}
          </p>
        ) : null}
      </div>

      <div className="mt-3 flex shrink-0 items-center gap-2 border-t pt-3" style={{ borderColor: border }}>
        <button
          type="button"
          onClick={() => void onSkip()}
          className="min-h-10 shrink-0 rounded-[10px] px-2 py-2 text-[11px] font-bold"
          style={{ color: muted }}
        >
          Skip
        </button>

        <div className="ml-auto flex min-w-0 items-center justify-end gap-2">
          {index > 0 ? (
            <button
              type="button"
              onClick={() => go(index - 1)}
              className="min-h-10 rounded-[10px] border px-3 py-2 text-[11px] font-black"
              style={{ borderColor: border, color: text }}
            >
              Back
            </button>
          ) : null}
          <button
            type="button"
            onClick={handlePrimary}
            className="min-h-10 rounded-[10px] px-4 py-2 text-[11px] font-black text-white"
            style={{ background: accent }}
          >
            {step.nextLabel || (isLast ? 'Start Fishing' : 'Next')}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const overlay = (
    <div className="fixed inset-0 z-[2000] pointer-events-none" aria-live="polite">
      <style>{`
        @keyframes roddoTourWaterRipple {
          0% {
            transform: translate(-50%, -50%) scale(.86);
            opacity: 0;
            box-shadow: 0 0 6px ${accentGlow};
          }
          18% {
            opacity: .82;
          }
          62% {
            opacity: .34;
            box-shadow: 0 0 12px ${accentGlow};
          }
          100% {
            transform: translate(-50%, -50%) scale(1.58);
            opacity: 0;
            box-shadow: 0 0 18px rgba(0,0,0,0);
          }
        }
        @keyframes roddoTourCardIn {
          from { opacity: 0; transform: translate(-50%, calc(-50% + 8px)); }
          to { opacity: 1; transform: translate(-50%, -50%); }
        }
        .roddo-tour-card { animation: roddoTourCardSoftIn 180ms ease-out both; }
        @keyframes roddoTourCardSoftIn {
          from { opacity: 0; transform: translate(-50%, calc(-50% + 6px)); }
          to { opacity: 1; transform: translate(-50%, -50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .roddo-tour-pulse-ring { animation: none !important; opacity: .78 !important; }
          .roddo-tour-card { animation: none !important; }
        }
      `}</style>

      {!isButtonIntro ? (
        <div
          className="absolute inset-0"
          style={{ background: demoTint }}
          aria-hidden="true"
        />
      ) : null}

      {isButtonIntro ? (
        <>
          {rect ? (
            <div
              className="absolute inset-0"
              aria-hidden="true"
              style={{
                background: dim,
                WebkitMaskImage: `radial-gradient(circle ${Math.max(34, targetPulseSize * 0.58)}px at ${pulseX}px ${pulseY}px, transparent 0, transparent 68%, #000 76%)`,
                maskImage: `radial-gradient(circle ${Math.max(34, targetPulseSize * 0.58)}px at ${pulseX}px ${pulseY}px, transparent 0, transparent 68%, #000 76%)`,
              }}
            />
          ) : (
            <div className="absolute inset-0" style={{ background: dim }} />
          )}

          {rect ? (
            <>
              <span
                className="roddo-tour-pulse-ring absolute rounded-full border"
                aria-hidden="true"
                style={{
                  left: pulseX,
                  top: pulseY,
                  width: targetPulseSize,
                  height: targetPulseSize,
                  borderColor: accent,
                  boxShadow: `0 0 10px ${accentGlow}`,
                  animation: 'roddoTourWaterRipple 1.85s cubic-bezier(.18,.72,.24,1) infinite',
                  willChange: 'transform, opacity, box-shadow',
                }}
              />
            </>
          ) : null}
        </>
      ) : null}

      {card}
    </div>
  );

  return createPortal(overlay, document.body);
}
